import React, {
  useRef,
  useContext,
  createContext,
  useState,
  useEffect,
} from 'react'
import _ from 'lodash'
import { useMediaQueryScreen } from '../../hooks/useMediaQuery'
import usePersistState from '../../hooks/usePersistState'

const NavContext = createContext()

export const useNav = () => {
  return useContext(NavContext)
}

export const NavProvider = ({
  menuItems,
  userName,
  firstName,
  userGoal,
  userTimeline,
  startExpanded = true,
  isSilverOrAbove,
  isGoldOrAbove,
  isNotGlobalProgramLevel,
  isNotGlobalProgramLevelAndIsSilverOrAbove,
  dynamicLinks,
  currentProgramLevel,
  candidateBelongsToPartnerCompany,
  partnerHasUpcomingEvents,
  platformName,
  clientAdminProfileRole,
  children,
}) => {
  const navItems = menuItems.navItems

  const navItemsArr = Object.values(navItems)

  const pathHasMatchingPath = (item, path, includeQueryParams = false) => {
    const matchingPaths = item.matching_paths || []
    // Remove both query string and hash if we're not including query params
    const cleanPath = includeQueryParams ? path : path.split(/[?#]/)[0]
    return matchingPaths.some((pathToCompare) =>
      cleanPath.includes(pathToCompare),
    )
  }

  const getCleanedArr = (arr) => {
    if (isGoldOrAbove) {
      return arr
    }
    return arr.filter((c) => c.slug !== 'professionalServices')
  }

  const [activeNav, setActiveNav] = useState(null)
  const [activeSubNav, setActiveSubNav] = useState(null)

  const updateActiveNav = () => {
    let activeNavItemSlug
    let activeSubNavItemSlug
    let path = window.location.pathname
    const fullPath = path + window.location.search + window.location.hash

    const basePath = path.split('/').slice(0, -1).join('/')

    navItemsArr.forEach((navItem) => {
      const children = navItem.children

      children.forEach((item) => {
        if (basePath === '/coaching/resources/article') {
          activeNavItemSlug = 'guidesMiniPlatform'
        } else if (
          item.path === fullPath ||
          pathHasMatchingPath(item, fullPath, true) ||
          item.path === path ||
          pathHasMatchingPath(item, path)
        ) {
          if (!item.isMobileMenu || !activeNavItemSlug) {
            activeNavItemSlug = item.slug
          }
        }
        if (item.children) {
          getCleanedArr(item.children).forEach((subItem) => {
            if (
              subItem.path === fullPath ||
              pathHasMatchingPath(subItem, fullPath, true) ||
              subItem.path === path ||
              pathHasMatchingPath(subItem, path)
            ) {
              activeSubNavItemSlug = subItem.slug
              activeNavItemSlug = item.slug
            }
          })
        }
      })
    })

    setActiveNav(activeNavItemSlug)
    setActiveSubNav(activeSubNavItemSlug)
  }

  useEffect(() => {
    // Initial update
    updateActiveNav()

    // Listen for hash changes
    const handleHashChange = () => {
      updateActiveNav()
    }

    window.addEventListener('hashchange', handleHashChange)
    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [])

  const isMobile = !useMediaQueryScreen('sm')
  const [expanded, setIsExpanded] = usePersistState(startExpanded, 'isExpanded')
  const isExpanded = isMobile ? true : expanded
  const [popupSubNav, setPopupSubNav] = useState(null)
  const currentTimer = useRef(null)
  const showGoal = menuItems.showGoal === true
  const showUser = menuItems.showUser === true

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const navigateTo = (item) => {
    var href = getLink(item)
    if (href) {
      window.location.href = href
    }
  }

  const getLink = (item) => {
    const { dynamicLink, path } = item
    if (dynamicLink) {
      return dynamicLinks[dynamicLink]
    } else if (path) {
      return path
    }
  }

  const meetsMinimumProgramLevel = (programLevel) => {
    if (!programLevel) {
      return true
    }
    if (programLevel === 'silverOrAbove') {
      return isSilverOrAbove
    } else if (programLevel === 'goldOrAbove') {
      return isGoldOrAbove
    } else if (programLevel === 'notGlobalProgramLevel') {
      return isNotGlobalProgramLevel
    } else if (programLevel === 'notGlobalProgramLevelAndIsSilverOrAbove') {
      return isNotGlobalProgramLevelAndIsSilverOrAbove
    } else {
      console.log('program level not found', programLevel)
      return true
    }
  }

  const hasProgramLevel = (programLevel) => {
    // console.log('hasProgramLevel', currentProgramLevel, programLevel)
    const CT_PROGRAM_LEVELS = {
      CONNECT: 'SILVER',
      ADVANCE: 'GOLD',
      CONCIERGE: 'SELECT',
    }
    return (
      currentProgramLevel === programLevel ||
      CT_PROGRAM_LEVELS[currentProgramLevel] === programLevel
    )
  }

  const value = {
    navItems,
    userName,
    firstName,
    getLink,
    isExpanded,
    toggleExpanded,
    activeSubNav,
    setActiveSubNav,
    activeNav,
    setActiveNav,
    navigateTo,
    popupSubNav,
    setPopupSubNav,
    currentTimer,
    meetsMinimumProgramLevel,
    isMobile,
    showGoal,
    showUser,
    userGoal,
    userTimeline,
    hasProgramLevel,
    candidateBelongsToPartnerCompany,
    partnerHasUpcomingEvents,
    platformName,
    clientAdminProfileRole,
  }

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>
}
